var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { matrixPool } from '../utils';
import { getAnchor } from './router';
/** Convert world coordinates to a parent group's local space */
export function worldToParentLocal(parent, wx, wy) {
    var wm = parent.getWorldMatrix();
    var inv = matrixPool.acquire();
    if (!wm.invertInto(inv)) {
        matrixPool.release(inv);
        return { x: wx, y: wy };
    }
    var local = inv.transformPoint(wx, wy);
    matrixPool.release(inv);
    return local;
}
/** Convert obstacle bbox from world space to parent local space */
export function obstacleToParentLocal(parent, obs) {
    var tl = worldToParentLocal(parent, obs.x, obs.y);
    var br = worldToParentLocal(parent, obs.x + obs.width, obs.y + obs.height);
    return {
        x: Math.min(tl.x, br.x),
        y: Math.min(tl.y, br.y),
        width: Math.abs(br.x - tl.x),
        height: Math.abs(br.y - tl.y),
    };
}
/**
 * Card size from metadata — ignores nested child groups (org/mind leaves).
 * Critical for correct connector anchors on nested diagram trees.
 */
export function getDiagramCardSize(node) {
    var _a, _b, _c, _d, _e, _f;
    var w = (_b = (_a = node.metadata) === null || _a === void 0 ? void 0 : _a.orgCardWidth) !== null && _b !== void 0 ? _b : (_c = node.metadata) === null || _c === void 0 ? void 0 : _c.diagramCardWidth;
    var h = (_e = (_d = node.metadata) === null || _d === void 0 ? void 0 : _d.orgCardHeight) !== null && _e !== void 0 ? _e : (_f = node.metadata) === null || _f === void 0 ? void 0 : _f.diagramCardHeight;
    if (typeof w === 'number' && w > 0 && typeof h === 'number' && h > 0) {
        return { width: w, height: h };
    }
    return null;
}
/**
 * Accumulate node.x/y from `node` up to (but not including) `parent`.
 * Uses live position values — correct during drag even if world matrices are stale.
 */
export function positionInParent(node, parent) {
    var x = 0;
    var y = 0;
    var cur = node;
    while (cur && cur !== parent) {
        x += cur.x;
        y += cur.y;
        cur = cur.parent;
    }
    return { x: x, y: y };
}
/**
 * Map a point in the node's unscaled local card space into parent-local coords,
 * applying the node's scale then rotation (same order as Node.getLocalMatrix).
 */
export function nodeLocalToParent(node, parent, lx, ly) {
    var pos = positionInParent(node, parent);
    var sx = Number.isFinite(node.scaleX) && node.scaleX !== 0 ? node.scaleX : 1;
    var sy = Number.isFinite(node.scaleY) && node.scaleY !== 0 ? node.scaleY : 1;
    var rx = lx * sx;
    var ry = ly * sy;
    var rad = ((node.rotation || 0) * Math.PI) / 180;
    if (!rad)
        return { x: pos.x + rx, y: pos.y + ry };
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    return {
        x: pos.x + rx * cos - ry * sin,
        y: pos.y + rx * sin + ry * cos,
    };
}
/** Local content box of a node (card metadata or children AABB), ignoring nested diagram nodes. */
export function getLocalNodeBox(node) {
    var e_1, _a;
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    var sx = Number.isFinite(node.scaleX) && node.scaleX !== 0 ? node.scaleX : 1;
    var sy = Number.isFinite(node.scaleY) && node.scaleY !== 0 ? node.scaleY : 1;
    var card = getDiagramCardSize(node);
    if (card)
        return { x: 0, y: 0, width: card.width * sx, height: card.height * sy };
    var group = node;
    if (!Array.isArray(group.children) || group.children.length === 0) {
        var b = node.getBounds();
        // getBounds on leaves is local; on groups it may be world — prefer a safe fallback
        if (node.type !== 'group') {
            return {
                x: b.x * sx,
                y: b.y * sy,
                width: Math.max(b.width, 24) * sx,
                height: Math.max(b.height, 24) * sy,
            };
        }
        return { x: 0, y: 0, width: 40 * sx, height: 32 * sy };
    }
    var minX = Infinity;
    var minY = Infinity;
    var maxX = -Infinity;
    var maxY = -Infinity;
    try {
        for (var _m = __values(group.children), _o = _m.next(); !_o.done; _o = _m.next()) {
            var child = _o.value;
            if ((_b = child.metadata) === null || _b === void 0 ? void 0 : _b.isDiagramHitTarget)
                continue;
            if ((_c = child.metadata) === null || _c === void 0 ? void 0 : _c.orgNode)
                continue;
            if ((_d = child.metadata) === null || _d === void 0 ? void 0 : _d.diagramEditorOverlay)
                continue;
            if (child.visible === false)
                continue;
            var cw = (_f = (_e = child.metadata) === null || _e === void 0 ? void 0 : _e.orgCardWidth) !== null && _f !== void 0 ? _f : (_g = child.metadata) === null || _g === void 0 ? void 0 : _g.diagramCardWidth;
            var ch = (_j = (_h = child.metadata) === null || _h === void 0 ? void 0 : _h.orgCardHeight) !== null && _j !== void 0 ? _j : (_k = child.metadata) === null || _k === void 0 ? void 0 : _k.diagramCardHeight;
            if (typeof cw === 'number' && typeof ch === 'number') {
                minX = Math.min(minX, child.x);
                minY = Math.min(minY, child.y);
                maxX = Math.max(maxX, child.x + cw);
                maxY = Math.max(maxY, child.y + ch);
                continue;
            }
            if (child.type === 'group' && ((_l = child.children) === null || _l === void 0 ? void 0 : _l.length))
                continue;
            var cb = child.getBounds();
            minX = Math.min(minX, child.x + cb.x);
            minY = Math.min(minY, child.y + cb.y);
            maxX = Math.max(maxX, child.x + cb.x + Math.max(cb.width, 1));
            maxY = Math.max(maxY, child.y + cb.y + Math.max(cb.height, 1));
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_o && !_o.done && (_a = _m.return)) _a.call(_m);
        }
        finally { if (e_1) throw e_1.error; }
    }
    if (!Number.isFinite(minX))
        return { x: 0, y: 0, width: 40 * sx, height: 32 * sy };
    return {
        x: minX * sx,
        y: minY * sy,
        width: Math.max(maxX - minX, 24) * sx,
        height: Math.max(maxY - minY, 24) * sy,
    };
}
/** Node content box in a parent group's local coordinates (drag-safe, rotation-aware AABB). */
export function getNodeBoxInParent(node, parent) {
    var e_2, _a;
    var card = getDiagramCardSize(node);
    if (card) {
        var w = card.width;
        var h = card.height;
        var corners = [
            nodeLocalToParent(node, parent, 0, 0),
            nodeLocalToParent(node, parent, w, 0),
            nodeLocalToParent(node, parent, w, h),
            nodeLocalToParent(node, parent, 0, h),
        ];
        var minX = Infinity;
        var minY = Infinity;
        var maxX = -Infinity;
        var maxY = -Infinity;
        try {
            for (var corners_1 = __values(corners), corners_1_1 = corners_1.next(); !corners_1_1.done; corners_1_1 = corners_1.next()) {
                var c = corners_1_1.value;
                minX = Math.min(minX, c.x);
                minY = Math.min(minY, c.y);
                maxX = Math.max(maxX, c.x);
                maxY = Math.max(maxY, c.y);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (corners_1_1 && !corners_1_1.done && (_a = corners_1.return)) _a.call(corners_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return {
            x: minX,
            y: minY,
            width: Math.max(maxX - minX, 1),
            height: Math.max(maxY - minY, 1),
        };
    }
    var pos = positionInParent(node, parent);
    var box = getLocalNodeBox(node);
    // Fallback without card metadata: axis-aligned (legacy)
    return {
        x: pos.x + box.x,
        y: pos.y + box.y,
        width: box.width,
        height: box.height,
    };
}
/** Transform a point in node-local space into a parent group's local space */
export function localPointToParent(node, parent, lx, ly) {
    return nodeLocalToParent(node, parent, lx, ly);
}
/**
 * Connection port on a node edge, in parent local space — respects rotation.
 * @param along 0..1 position along the side (0.5 = mid). Top/bottom: left→right;
 *              left/right: top→bottom. Ignored for `center`.
 */
export function getCardSideAnchor(node, parent, side, along) {
    var _a;
    if (along === void 0) { along = 0.5; }
    var card = (_a = getDiagramCardSize(node)) !== null && _a !== void 0 ? _a : { width: 40, height: 32 };
    var w = card.width;
    var h = card.height;
    var t = Math.max(0.05, Math.min(0.95, along));
    var lx = w / 2;
    var ly = h / 2;
    if (side === 'top') {
        lx = w * t;
        ly = 0;
    }
    else if (side === 'bottom') {
        lx = w * t;
        ly = h;
    }
    else if (side === 'left') {
        lx = 0;
        ly = h * t;
    }
    else if (side === 'right') {
        lx = w;
        ly = h * t;
    }
    return nodeLocalToParent(node, parent, lx, ly);
}
/**
 * Pick the best pair of connection ports so the wire attaches to node edges
 * and stretches as nodes move (draw.io-style).
 */
export function pickConnectionSides(fromBox, toBox) {
    var fromCx = fromBox.x + fromBox.width / 2;
    var fromCy = fromBox.y + fromBox.height / 2;
    var toCx = toBox.x + toBox.width / 2;
    var toCy = toBox.y + toBox.height / 2;
    var dx = toCx - fromCx;
    var dy = toCy - fromCy;
    if (Math.abs(dx) > Math.abs(dy) * 1.05) {
        return {
            fromSide: dx >= 0 ? 'right' : 'left',
            toSide: dx >= 0 ? 'left' : 'right',
        };
    }
    if (Math.abs(dy) > Math.abs(dx) * 1.05) {
        return {
            fromSide: dy >= 0 ? 'bottom' : 'top',
            toSide: dy >= 0 ? 'top' : 'bottom',
        };
    }
    // Near-diagonal: prefer vertical stack (TB) for flowchart-like layouts
    if (Math.abs(dy) >= Math.abs(dx)) {
        return {
            fromSide: dy >= 0 ? 'bottom' : 'top',
            toSide: dy >= 0 ? 'top' : 'bottom',
        };
    }
    return {
        fromSide: dx >= 0 ? 'right' : 'left',
        toSide: dx >= 0 ? 'left' : 'right',
    };
}
/** Card-only obstacle box in parent-local space (excludes nested children). */
export function getCardObstacleInParent(node, parent) {
    var box = getNodeBoxInParent(node, parent);
    if (box.width <= 0 || box.height <= 0)
        return null;
    return { x: box.x, y: box.y, width: box.width, height: box.height };
}
/** Card-only obstacle box in world space (excludes nested children). */
export function getCardObstacle(node) {
    var card = getDiagramCardSize(node);
    if (!card) {
        var b = node.getBounds();
        if (b.width <= 0 || b.height <= 0)
            return null;
        return { x: b.x, y: b.y, width: b.width, height: b.height };
    }
    var tl = node.getWorldMatrix().transformPoint(0, 0);
    var br = node.getWorldMatrix().transformPoint(card.width, card.height);
    return {
        x: Math.min(tl.x, br.x),
        y: Math.min(tl.y, br.y),
        width: Math.abs(br.x - tl.x),
        height: Math.abs(br.y - tl.y),
    };
}
/**
 * Anchor points between two nodes in parent group's local coordinates.
 * Chooses facing mid-side ports on the rotated cards so wires attach cleanly
 * after move/resize/rotate; users can still add manual bend points later.
 *
 * Optional `fromAlong` / `toAlong` (0..1) fan multiple wires off the same side
 * so decision branches and parallel connectors do not stack on one point.
 */
export function getConnectorAnchors(from, to, parent, opts) {
    var e_3, _a, e_4, _b;
    var _c, _d;
    var sides = ['top', 'right', 'bottom', 'left'];
    var fromC = getCardSideAnchor(from, parent, 'center');
    var toC = getCardSideAnchor(to, parent, 'center');
    var bestFrom = 'right';
    var bestTo = 'left';
    var bestScore = Infinity;
    try {
        for (var sides_1 = __values(sides), sides_1_1 = sides_1.next(); !sides_1_1.done; sides_1_1 = sides_1.next()) {
            var fromSide = sides_1_1.value;
            var a_1 = getCardSideAnchor(from, parent, fromSide);
            var outFrom = (a_1.x - fromC.x) * (toC.x - fromC.x) + (a_1.y - fromC.y) * (toC.y - fromC.y);
            try {
                for (var sides_2 = (e_4 = void 0, __values(sides)), sides_2_1 = sides_2.next(); !sides_2_1.done; sides_2_1 = sides_2.next()) {
                    var toSide = sides_2_1.value;
                    var b_1 = getCardSideAnchor(to, parent, toSide);
                    var outTo = (b_1.x - toC.x) * (fromC.x - toC.x) + (b_1.y - toC.y) * (fromC.y - toC.y);
                    var dist = Math.hypot(a_1.x - b_1.x, a_1.y - b_1.y);
                    // Prefer ports that face the other node; heavily penalize inward ports
                    var facePenalty = (outFrom < 0 ? 400 : 0) + (outTo < 0 ? 400 : 0);
                    var score = dist + facePenalty;
                    if (score < bestScore) {
                        bestScore = score;
                        bestFrom = fromSide;
                        bestTo = toSide;
                    }
                }
            }
            catch (e_4_1) { e_4 = { error: e_4_1 }; }
            finally {
                try {
                    if (sides_2_1 && !sides_2_1.done && (_b = sides_2.return)) _b.call(sides_2);
                }
                finally { if (e_4) throw e_4.error; }
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (sides_1_1 && !sides_1_1.done && (_a = sides_1.return)) _a.call(sides_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    // Fallback when all ports score poorly: AABB heuristic
    if (!Number.isFinite(bestScore) || bestScore > 1e9) {
        var fromBox = getNodeBoxInParent(from, parent);
        var toBox = getNodeBoxInParent(to, parent);
        var picked = pickConnectionSides(fromBox, toBox);
        bestFrom = picked.fromSide;
        bestTo = picked.toSide;
    }
    var fromAlong = (_c = opts === null || opts === void 0 ? void 0 : opts.fromAlong) !== null && _c !== void 0 ? _c : 0.5;
    var toAlong = (_d = opts === null || opts === void 0 ? void 0 : opts.toAlong) !== null && _d !== void 0 ? _d : 0.5;
    var a = getCardSideAnchor(from, parent, bestFrom, fromAlong);
    var b = getCardSideAnchor(to, parent, bestTo, toAlong);
    return { x1: a.x, y1: a.y, x2: b.x, y2: b.y, fromSide: bestFrom, toSide: bestTo };
}
/** Evenly spaced `along` values in (0.2, 0.8) so stacked ports stay on the card edge. */
export function fanAlongSlots(count) {
    if (count <= 1)
        return [0.5];
    var lo = 0.2;
    var hi = 0.8;
    return Array.from({ length: count }, function (_, i) { return lo + ((hi - lo) * i) / (count - 1); });
}
/**
 * Assign connection ports so wires use free sides of each symbol when possible.
 *
 * Rules:
 * 1. Each edge prefers the side facing its peer (left target → left port, etc.).
 * 2. Edges that share the same side fan along that side (distinct along slots).
 * 3. Parallel routes get a railOffset so mid-segments do not paint on top of each other.
 *
 * Does not force every outbound edge onto one side — free sides stay available.
 */
export function planEdgeFanAnchors(edges, parent) {
    var e_5, _a, e_6, _b, e_7, _c, e_8, _d, e_9, _e, e_10, _f, e_11, _g;
    var _h, _j, _k, _l, _m;
    var prelim = edges.map(function (e) {
        var _a, _b, _c, _d, _e, _f;
        var fromC = getCardSideAnchor(e.from, parent, 'center');
        var toC = getCardSideAnchor(e.to, parent, 'center');
        var fromSide = preferredExitSide(toC.x - fromC.x, toC.y - fromC.y);
        var toSide = preferredExitSide(fromC.x - toC.x, fromC.y - toC.y);
        return {
            key: e.key,
            from: e.from,
            to: e.to,
            fromId: String((_c = (_b = (_a = e.from.metadata) === null || _a === void 0 ? void 0 : _a.diagramId) !== null && _b !== void 0 ? _b : e.from.id) !== null && _c !== void 0 ? _c : ''),
            toId: String((_f = (_e = (_d = e.to.metadata) === null || _d === void 0 ? void 0 : _d.diagramId) !== null && _e !== void 0 ? _e : e.to.id) !== null && _f !== void 0 ? _f : ''),
            fromSide: fromSide,
            toSide: toSide,
        };
    });
    var fromAlong = new Map();
    var toAlong = new Map();
    var railOffset = new Map();
    // Fan along each (node, side) group — do NOT collapse all edges onto one side
    var fromGroups = new Map();
    try {
        for (var prelim_1 = __values(prelim), prelim_1_1 = prelim_1.next(); !prelim_1_1.done; prelim_1_1 = prelim_1.next()) {
            var e = prelim_1_1.value;
            var gkey = "".concat(e.fromId, "::").concat(e.fromSide);
            var list = (_h = fromGroups.get(gkey)) !== null && _h !== void 0 ? _h : [];
            list.push(e);
            fromGroups.set(gkey, list);
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (prelim_1_1 && !prelim_1_1.done && (_a = prelim_1.return)) _a.call(prelim_1);
        }
        finally { if (e_5) throw e_5.error; }
    }
    var _loop_1 = function (group) {
        var side = group[0].fromSide;
        group.sort(function (a, b) { return sortPeersAlongSide(a.to, b.to, parent, side); });
        var slots = fanAlongSlots(group.length);
        group.forEach(function (e, i) { return fromAlong.set(e.key, slots[i]); });
    };
    try {
        for (var fromGroups_1 = __values(fromGroups), fromGroups_1_1 = fromGroups_1.next(); !fromGroups_1_1.done; fromGroups_1_1 = fromGroups_1.next()) {
            var _o = __read(fromGroups_1_1.value, 2), group = _o[1];
            _loop_1(group);
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (fromGroups_1_1 && !fromGroups_1_1.done && (_b = fromGroups_1.return)) _b.call(fromGroups_1);
        }
        finally { if (e_6) throw e_6.error; }
    }
    var toGroups = new Map();
    try {
        for (var prelim_2 = __values(prelim), prelim_2_1 = prelim_2.next(); !prelim_2_1.done; prelim_2_1 = prelim_2.next()) {
            var e = prelim_2_1.value;
            var gkey = "".concat(e.toId, "::").concat(e.toSide);
            var list = (_j = toGroups.get(gkey)) !== null && _j !== void 0 ? _j : [];
            list.push(e);
            toGroups.set(gkey, list);
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (prelim_2_1 && !prelim_2_1.done && (_c = prelim_2.return)) _c.call(prelim_2);
        }
        finally { if (e_7) throw e_7.error; }
    }
    var _loop_2 = function (group) {
        var side = group[0].toSide;
        group.sort(function (a, b) { return sortPeersAlongSide(a.from, b.from, parent, side); });
        var slots = fanAlongSlots(group.length);
        group.forEach(function (e, i) { return toAlong.set(e.key, slots[i]); });
    };
    try {
        for (var toGroups_1 = __values(toGroups), toGroups_1_1 = toGroups_1.next(); !toGroups_1_1.done; toGroups_1_1 = toGroups_1.next()) {
            var _p = __read(toGroups_1_1.value, 2), group = _p[1];
            _loop_2(group);
        }
    }
    catch (e_8_1) { e_8 = { error: e_8_1 }; }
    finally {
        try {
            if (toGroups_1_1 && !toGroups_1_1.done && (_d = toGroups_1.return)) _d.call(toGroups_1);
        }
        finally { if (e_8) throw e_8.error; }
    }
    var _loop_3 = function (group) {
        if (group.length <= 1) {
            railOffset.set(group[0].key, 0);
            return "continue";
        }
        var gap = 14;
        var mid = (group.length - 1) / 2;
        group.forEach(function (e, i) {
            railOffset.set(e.key, (i - mid) * gap);
        });
    };
    try {
        // Lane offsets for edges that share an exit corridor (same from node + side)
        for (var fromGroups_2 = __values(fromGroups), fromGroups_2_1 = fromGroups_2.next(); !fromGroups_2_1.done; fromGroups_2_1 = fromGroups_2.next()) {
            var _q = __read(fromGroups_2_1.value, 2), group = _q[1];
            _loop_3(group);
        }
    }
    catch (e_9_1) { e_9 = { error: e_9_1 }; }
    finally {
        try {
            if (fromGroups_2_1 && !fromGroups_2_1.done && (_e = fromGroups_2.return)) _e.call(fromGroups_2);
        }
        finally { if (e_9) throw e_9.error; }
    }
    try {
        for (var prelim_3 = __values(prelim), prelim_3_1 = prelim_3.next(); !prelim_3_1.done; prelim_3_1 = prelim_3.next()) {
            var e = prelim_3_1.value;
            if (!railOffset.has(e.key))
                railOffset.set(e.key, 0);
        }
    }
    catch (e_10_1) { e_10 = { error: e_10_1 }; }
    finally {
        try {
            if (prelim_3_1 && !prelim_3_1.done && (_f = prelim_3.return)) _f.call(prelim_3);
        }
        finally { if (e_10) throw e_10.error; }
    }
    var out = new Map();
    try {
        for (var prelim_4 = __values(prelim), prelim_4_1 = prelim_4.next(); !prelim_4_1.done; prelim_4_1 = prelim_4.next()) {
            var e = prelim_4_1.value;
            var fa = (_k = fromAlong.get(e.key)) !== null && _k !== void 0 ? _k : 0.5;
            var ta = (_l = toAlong.get(e.key)) !== null && _l !== void 0 ? _l : 0.5;
            var a = getCardSideAnchor(e.from, parent, e.fromSide, fa);
            var b = getCardSideAnchor(e.to, parent, e.toSide, ta);
            out.set(e.key, {
                fromAlong: fa,
                toAlong: ta,
                fromSide: e.fromSide,
                toSide: e.toSide,
                x1: a.x,
                y1: a.y,
                x2: b.x,
                y2: b.y,
                railOffset: (_m = railOffset.get(e.key)) !== null && _m !== void 0 ? _m : 0,
            });
        }
    }
    catch (e_11_1) { e_11 = { error: e_11_1 }; }
    finally {
        try {
            if (prelim_4_1 && !prelim_4_1.done && (_g = prelim_4.return)) _g.call(prelim_4);
        }
        finally { if (e_11) throw e_11.error; }
    }
    // If an arrow/edge would override a symbol border or land on an occupied port,
    // snap that end to the nearest free port on the same symbol.
    return resolvePortsAwayFromBorderOverrides(out, prelim, parent);
}
/** Sample ports around a card border (4 sides × several along slots). */
export function enumerateCardPorts(node, parent, alongSamples) {
    var e_12, _a, e_13, _b;
    if (alongSamples === void 0) { alongSamples = [0.15, 0.3, 0.5, 0.7, 0.85]; }
    var sides = ['top', 'right', 'bottom', 'left'];
    var out = [];
    try {
        for (var sides_3 = __values(sides), sides_3_1 = sides_3.next(); !sides_3_1.done; sides_3_1 = sides_3.next()) {
            var side = sides_3_1.value;
            try {
                for (var alongSamples_1 = (e_13 = void 0, __values(alongSamples)), alongSamples_1_1 = alongSamples_1.next(); !alongSamples_1_1.done; alongSamples_1_1 = alongSamples_1.next()) {
                    var along = alongSamples_1_1.value;
                    var p = getCardSideAnchor(node, parent, side, along);
                    out.push({ side: side, along: along, x: p.x, y: p.y });
                }
            }
            catch (e_13_1) { e_13 = { error: e_13_1 }; }
            finally {
                try {
                    if (alongSamples_1_1 && !alongSamples_1_1.done && (_b = alongSamples_1.return)) _b.call(alongSamples_1);
                }
                finally { if (e_13) throw e_13.error; }
            }
        }
    }
    catch (e_12_1) { e_12 = { error: e_12_1 }; }
    finally {
        try {
            if (sides_3_1 && !sides_3_1.done && (_a = sides_3.return)) _a.call(sides_3);
        }
        finally { if (e_12) throw e_12.error; }
    }
    return out;
}
function portOccupancyRadius(node, parent) {
    var box = getNodeBoxInParent(node, parent);
    return Math.max(14, Math.min(28, Math.min(box.width, box.height) * 0.18));
}
function portsTooClose(ax, ay, bx, by, minDist) {
    return Math.hypot(ax - bx, ay - by) < minDist;
}
/**
 * True when the first route stub from an attachment point goes into the card
 * interior (arrow/edge overrides the border) instead of leaving cleanly.
 */
export function attachmentOverridesBorder(attachX, attachY, peerX, peerY, box, side) {
    var outward = side === 'top'
        ? { x: 0, y: -1 }
        : side === 'bottom'
            ? { x: 0, y: 1 }
            : side === 'left'
                ? { x: -1, y: 0 }
                : { x: 1, y: 0 };
    var towardPeerX = peerX - attachX;
    var towardPeerY = peerY - attachY;
    var plen = Math.hypot(towardPeerX, towardPeerY) || 1;
    // Positive = leaving the card toward the peer; negative = aiming back into the symbol
    var outwardDot = outward.x * (towardPeerX / plen) + outward.y * (towardPeerY / plen);
    if (outwardDot < -0.2)
        return true;
    // Attachment must sit on the border, not deep inside the fill
    var slop = 4;
    var onBorder = Math.abs(attachX - box.x) <= slop ||
        Math.abs(attachX - (box.x + box.width)) <= slop ||
        Math.abs(attachY - box.y) <= slop ||
        Math.abs(attachY - (box.y + box.height)) <= slop;
    var deepInside = attachX > box.x + slop &&
        attachX < box.x + box.width - slop &&
        attachY > box.y + slop &&
        attachY < box.y + box.height - slop;
    if (deepInside && !onBorder)
        return true;
    return false;
}
/**
 * Pick nearest free port on the same symbol that faces the peer and is not
 * already taken by another edge endpoint.
 */
export function nearestFreePortOnSymbol(node, parent, peerX, peerY, occupied, preferSide) {
    var e_14, _a, e_15, _b;
    var box = getNodeBoxInParent(node, parent);
    var minDist = portOccupancyRadius(node, parent);
    var center = getCardSideAnchor(node, parent, 'center');
    var faceSide = preferredExitSide(peerX - center.x, peerY - center.y);
    var preferred = preferSide !== null && preferSide !== void 0 ? preferSide : faceSide;
    var candidates = enumerateCardPorts(node, parent);
    // Rank: free → facing side → not overriding → closest to ideal mid of preferred side
    var ideal = getCardSideAnchor(node, parent, preferred, 0.5);
    var best = null;
    var bestScore = Infinity;
    var _loop_4 = function (c) {
        var taken = occupied.some(function (o) { return portsTooClose(c.x, c.y, o.x, o.y, minDist); });
        if (taken)
            return "continue";
        if (attachmentOverridesBorder(c.x, c.y, peerX, peerY, box, c.side))
            return "continue";
        var sidePenalty = c.side === preferred ? 0 : c.side === faceSide ? 40 : 120;
        var distIdeal = Math.hypot(c.x - ideal.x, c.y - ideal.y);
        var distPeer = Math.hypot(c.x - peerX, c.y - peerY);
        var score = sidePenalty + distIdeal * 0.5 + distPeer * 0.15;
        if (score < bestScore) {
            bestScore = score;
            best = c;
        }
    };
    try {
        for (var candidates_1 = __values(candidates), candidates_1_1 = candidates_1.next(); !candidates_1_1.done; candidates_1_1 = candidates_1.next()) {
            var c = candidates_1_1.value;
            _loop_4(c);
        }
    }
    catch (e_14_1) { e_14 = { error: e_14_1 }; }
    finally {
        try {
            if (candidates_1_1 && !candidates_1_1.done && (_a = candidates_1.return)) _a.call(candidates_1);
        }
        finally { if (e_14) throw e_14.error; }
    }
    // Fallback: any free port even if slightly imperfect
    if (!best) {
        var _loop_5 = function (c) {
            var taken = occupied.some(function (o) { return portsTooClose(c.x, c.y, o.x, o.y, minDist); });
            if (taken)
                return "continue";
            var dist = Math.hypot(c.x - ideal.x, c.y - ideal.y);
            if (dist < bestScore) {
                bestScore = dist;
                best = c;
            }
        };
        try {
            for (var candidates_2 = __values(candidates), candidates_2_1 = candidates_2.next(); !candidates_2_1.done; candidates_2_1 = candidates_2.next()) {
                var c = candidates_2_1.value;
                _loop_5(c);
            }
        }
        catch (e_15_1) { e_15 = { error: e_15_1 }; }
        finally {
            try {
                if (candidates_2_1 && !candidates_2_1.done && (_b = candidates_2.return)) _b.call(candidates_2);
            }
            finally { if (e_15) throw e_15.error; }
        }
    }
    return best;
}
/**
 * Second pass: when an endpoint overrides a symbol border or collides with
 * another arrow on the same component, reconnect to the nearest free port
 * on that same symbol.
 */
function resolvePortsAwayFromBorderOverrides(plan, prelim, parent) {
    var e_16, _a;
    var _b, _c;
    var occupiedByNode = new Map();
    var take = function (nodeId, x, y, key, end) {
        var _a;
        var list = (_a = occupiedByNode.get(nodeId)) !== null && _a !== void 0 ? _a : [];
        list.push({ x: x, y: y, key: key, end: end });
        occupiedByNode.set(nodeId, list);
    };
    // Process twice so target-side snaps can free source conflicts on the next pass
    for (var pass = 0; pass < 2; pass++) {
        occupiedByNode.clear();
        var _loop_6 = function (e) {
            var cur = plan.get(e.key);
            if (!cur)
                return "continue";
            var fromSide = cur.fromSide;
            var toSide = cur.toSide;
            var fromAlong = cur.fromAlong;
            var toAlong = cur.toAlong;
            var x1 = cur.x1;
            var y1 = cur.y1;
            var x2 = cur.x2;
            var y2 = cur.y2;
            var fromBox = getNodeBoxInParent(e.from, parent);
            var toBox = getNodeBoxInParent(e.to, parent);
            var fromMin = portOccupancyRadius(e.from, parent);
            var toMin = portOccupancyRadius(e.to, parent);
            var fromOcc = ((_b = occupiedByNode.get(e.fromId)) !== null && _b !== void 0 ? _b : []).map(function (o) { return ({ x: o.x, y: o.y }); });
            var toOcc = ((_c = occupiedByNode.get(e.toId)) !== null && _c !== void 0 ? _c : []).map(function (o) { return ({ x: o.x, y: o.y }); });
            var fromConflict = fromOcc.some(function (o) { return portsTooClose(x1, y1, o.x, o.y, fromMin); }) ||
                attachmentOverridesBorder(x1, y1, x2, y2, fromBox, fromSide);
            if (fromConflict) {
                var next = nearestFreePortOnSymbol(e.from, parent, x2, y2, fromOcc, fromSide);
                if (next) {
                    fromSide = next.side;
                    fromAlong = next.along;
                    x1 = next.x;
                    y1 = next.y;
                }
            }
            var toConflict = toOcc.some(function (o) { return portsTooClose(x2, y2, o.x, o.y, toMin); }) ||
                attachmentOverridesBorder(x2, y2, x1, y1, toBox, toSide);
            if (toConflict) {
                var next = nearestFreePortOnSymbol(e.to, parent, x1, y1, toOcc, toSide);
                if (next) {
                    toSide = next.side;
                    toAlong = next.along;
                    x2 = next.x;
                    y2 = next.y;
                }
            }
            plan.set(e.key, __assign(__assign({}, cur), { fromSide: fromSide, toSide: toSide, fromAlong: fromAlong, toAlong: toAlong, x1: x1, y1: y1, x2: x2, y2: y2 }));
            take(e.fromId, x1, y1, e.key, 'from');
            take(e.toId, x2, y2, e.key, 'to');
        };
        try {
            for (var prelim_5 = (e_16 = void 0, __values(prelim)), prelim_5_1 = prelim_5.next(); !prelim_5_1.done; prelim_5_1 = prelim_5.next()) {
                var e = prelim_5_1.value;
                _loop_6(e);
            }
        }
        catch (e_16_1) { e_16 = { error: e_16_1 }; }
        finally {
            try {
                if (prelim_5_1 && !prelim_5_1.done && (_a = prelim_5.return)) _a.call(prelim_5);
            }
            finally { if (e_16) throw e_16.error; }
        }
    }
    return plan;
}
function sortPeersAlongSide(a, b, parent, side) {
    var ac = getCardSideAnchor(a, parent, 'center');
    var bc = getCardSideAnchor(b, parent, 'center');
    if (side === 'top' || side === 'bottom')
        return ac.x - bc.x || ac.y - bc.y;
    return ac.y - bc.y || ac.x - bc.x;
}
/**
 * Prefer the card side facing the peer. Uses a lower lateral threshold so
 * left/right ports are chosen when free instead of stacking everything on bottom/top.
 */
export function preferredExitSide(dx, dy) {
    var ax = Math.abs(dx);
    var ay = Math.abs(dy);
    // Significant horizontal component → use free left/right ports
    if (ax >= ay * 0.55)
        return dx >= 0 ? 'right' : 'left';
    if (ay >= ax * 0.55)
        return dy >= 0 ? 'bottom' : 'top';
    if (ay >= ax)
        return dy >= 0 ? 'bottom' : 'top';
    return dx >= 0 ? 'right' : 'left';
}
/** @deprecated use preferredExitSide — kept for call-site clarity in older notes */
export function dominantSideFromDelta(dx, dy) {
    return preferredExitSide(dx, dy);
}
/** Legacy world-space anchors (when no parent group is provided). */
export function getConnectorAnchorsWorld(from, to) {
    var toB = to.getBounds();
    var anchorWorld = getAnchor(from, toB.x + toB.width / 2, toB.y + toB.height / 2);
    var toAnchorWorld = getAnchor(to, anchorWorld.x, anchorWorld.y);
    return { x1: anchorWorld.x, y1: anchorWorld.y, x2: toAnchorWorld.x, y2: toAnchorWorld.y };
}
